import { makeStyles } from "tss-react/mui";
import { z } from "zod";
import {
  ColorOptionLabel,
  MiBox,
  MiDetailFields,
  MiSectionHeader,
} from "@miplatform/components";
import {
  CASH_ON_DELIVERY_PAYMENT_ALLOWED,
  CONFIGURABLE_COLORS,
  INPUT_TYPES,
  INVOICE_PAYMENT_ALLOWED,
  INVOICE_PAYMENT_METHODS,
  ORDER_TYPES_OPTIONS,
  PLATFORM_MODULES_OPTIONS,
  SECTION_HEADER_VARIANTS,
} from "@miplatform/constants";
import { useEdit } from "@miplatform/hooks";
import { capitalize, simpleSort } from "@miplatform/utils";
import { mdiAbacus, mdiCircleMedium, mdiFormatListBulleted } from "@mdi/js";
import { Footer } from "@mibuildweb/components";

export function Details({
  orderTemplate = {},
  trades = [],
  onUpdate = () => null,
}) {
  const { classes } = useStyles();

  const paymentMethods = [];
  if (orderTemplate.codPaymentAllowed) {
    paymentMethods.push(CASH_ON_DELIVERY_PAYMENT_ALLOWED);
  }
  if (orderTemplate.invoicePaymentAllowed) {
    paymentMethods.push(INVOICE_PAYMENT_ALLOWED);
  }
  paymentMethods.sort((a, b) => simpleSort(a, b));

  const edit = useEdit({
    ...orderTemplate,
    autoComplete: !!orderTemplate.autoCompleteTime,
    module: orderTemplate.module,
    paymentMethods,
  });

  const isPaymentMethodSelected = edit.getValue("paymentMethods")?.length > 0;

  const detailFields = [
    {
      label: "Name",
      name: "name",
      beforeOnChange: capitalize,
      width: "33%",
      validationRule: z.string().min(1, "Order Type Name is required"),
    },
    {
      label: "Trade",
      name: "tradeId",
      fieldType: INPUT_TYPES.SELECT,
      options: trades,
      width: "33%",
      validationRule: z.string().min(1, "Trade Name is required"),
    },
    {
      label: "Abbr.",
      name: "abbreviation",
      beforeOnChange: (value) => value.toUpperCase(),
      width: "33%",
      validationRule: z
        .string()
        .min(2, "Abbreviation must be 2-3 characters")
        .max(3, "Abbreviation must be 2-3 characters"),
    },
    {
      label: "Color",
      name: "color",
      fieldType: INPUT_TYPES.SELECT,
      options: CONFIGURABLE_COLORS.map(({ color, label }) => ({
        label: <ColorOptionLabel label={label} color={color} />,
        value: color,
      })),
      width: "33%",
      validationRule: z.string().min(1, "Color is required"),
    },
    {
      label: "Follow-Up Default Offset Hours",
      name: "followUpDefaultOffsetHours",
      width: "33%",
      fieldType: INPUT_TYPES.NUMBER,
      validationRule: z
        .number()
        .optional()
        .refine((val) => !val || /^[0-9]{1,2}$/.test(val), {
          message: "Must be a number with at most two digits",
        })
        .or(z.literal("")),
    },
    {
      label: "Follow-Up Max Offset Hours",
      name: "followUpMaxOffsetHours",
      width: "33%",
      fieldType: INPUT_TYPES.NUMBER,
      validationRule: z
        .number()
        .optional()
        .refine((val) => !val || /^[0-9]+$/.test(val), {
          message: "Must be a number with at least one digit",
        })
        .or(z.literal("")),
    },
    {
      label: "Fulfillment Sequence",
      name: "fulfillmentSequence",
      width: "49%",
      fieldType: INPUT_TYPES.NUMBER,
      validationRule: z
        .number()
        .optional()
        .refine((val) => !val || /^[0-9]+$/.test(val), {
          message: "Must be a number",
        })
        .or(z.literal("")),
    },
    {
      label: "Max Times Put On Hold",
      name: "maxTimesPutOnHold",
      width: "49%",
      fieldType: INPUT_TYPES.NUMBER,
      validationRule: z
        .number()
        .optional()
        .refine((val) => !val || val >= 0, {
          message: "Must be a number greater than or equal to 0",
        })
        .or(z.literal("")),
    },
    {
      label: "Requires Approval",
      name: "requiresApproval",
      width: "10%",
      fieldType: INPUT_TYPES.CHECKBOX,
      padding: "8px 0px",
      validationRule: z.boolean().optional(),
    },
    {
      label: "Auto Complete",
      name: "autoComplete",
      width: "10%",
      fieldType: INPUT_TYPES.CHECKBOX,
      padding: "8px 0px",
      validationRule: z
        .boolean()
        .optional()
        .refine((value) => {
          if (value) return !!edit.getValue("autoCompleteTime");
          return true;
        }),
      value: edit.getValue("autoComplete"),
      handleChange: (e) => {
        const isChecked = e.target.checked;
        if (!isChecked) {
          edit.update({ autoCompleteTime: null, autoComplete: false });
          return;
        }
        edit.update({ autoComplete: isChecked });
      },
    },
    {
      label: "Auto Complete Time",
      name: "autoCompleteTime",
      width: "70%",
      fieldType: INPUT_TYPES.TIME_PICKER,
      strictInterval: true,
      timeSteps: { minutes: 15 },
      disabled: !edit.getValue("autoComplete"),
    },
    {
      label: "Confirmation Lead Days",
      name: "confirmationLeadDays",
      width: "49%",
      fieldType: INPUT_TYPES.NUMBER,
      validationRule: z
        .number()
        .optional()
        .refine((value) => value >= 0, {
          message: "Confirmation Lead Days must be greater than or equal to 0",
        })
        .or(z.literal("")),
    },
    {
      label: "Confirmation Lead Time",
      name: "confirmationLeadTime",
      width: "49%",
      fieldType: INPUT_TYPES.TIME,
      validationRule: z
        .string()
        .optional()
        .refine(
          (value) => {
            const [hours, minutes] = value.split(":").map(Number);
            return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
          },
          {
            message: "Confirmation Lead Time must be a valid time",
          }
        )
        .or(z.literal("")),
    },
    {
      label: "Approval Lead Days",
      name: "approvalLeadDays",
      width: "49%",
      fieldType: INPUT_TYPES.NUMBER,
      validationRule: z
        .number()
        .optional()
        .refine((value) => value >= 0, {
          message: "Approval Lead Days must be greater than or equal to 0",
        })
        .or(z.literal("")),
    },
    {
      label: "Approval Lead Time",
      name: "approvalLeadTime",
      width: "49%",
      fieldType: INPUT_TYPES.TIME,
      validationRule: z
        .string()
        .optional()
        .refine(
          (value) => {
            const [hours, minutes] = value.split(":").map(Number);
            return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
          },
          {
            message: "Approval Lead Time must be a valid time",
          }
        )
        .or(z.literal("")),
    },
    {
      label: "Reschedule lock threshold",
      name: "lockRescheduleThreshold",
      width: "49%",
      fieldType: INPUT_TYPES.NUMBER,
      validationRule: z.number(),
    },
    {
      label: "Module",
      name: "module",
      width: "49%",
      fieldType: INPUT_TYPES.SELECT,
      options: PLATFORM_MODULES_OPTIONS,
    },
    {
      label: "Order Type",
      name: "homePhase",
      width: "49%",
      fieldType: INPUT_TYPES.SELECT,
      options: ORDER_TYPES_OPTIONS,
    },
  ];

  const accountingFields = [
    {
      fieldType: INPUT_TYPES.CUSTOM,
      name: "sectionHeaderPayment",
      groupable: false,
      width: "100%",
      customComponent: (
        <MiSectionHeader
          title="Payment"
          leftIcon={mdiCircleMedium}
          variant={SECTION_HEADER_VARIANTS.CONTAINED}
        />
      ),
    },
    {
      label: "Payment Method",
      name: "paymentMethods",
      options: INVOICE_PAYMENT_METHODS,
      handleChange: (e) => {
        const selectedMethods = Array.from(e.target.value || []);
        const isCodSelected = selectedMethods.includes(
          CASH_ON_DELIVERY_PAYMENT_ALLOWED
        );
        const isInvoiceSelected = selectedMethods.includes(
          INVOICE_PAYMENT_ALLOWED
        );

        const updatedValues = {
          paymentMethods: selectedMethods.sort((a, b) => simpleSort(a, b)),
          codPaymentAllowed: isCodSelected,
          invoicePaymentAllowed: isInvoiceSelected,
        };

        if (selectedMethods.length === 0) {
          updatedValues.isPrebill = false;
        }
        edit.update({
          ...updatedValues,
        });
      },
      minWidth: "180px",
      fieldType: INPUT_TYPES.MULTI_SELECT,
      flexGrow: 0,
      padding: "0px 36px",
    },
    {
      fieldType: INPUT_TYPES.CUSTOM,
      groupable: false,
      name: "sectionHeaderReconciliation",
      width: "100%",
      customComponent: (
        <MiSectionHeader
          title="Reconciliation"
          leftIcon={mdiCircleMedium}
          variant={SECTION_HEADER_VARIANTS.CONTAINED}
        />
      ),
    },
    {
      label: "Reconciliation Required",
      name: "isPrebill",
      readOnly: !isPaymentMethodSelected,
      fieldType: INPUT_TYPES.CHECKBOX,
      handleChange: (e) => edit.update({ isPrebill: e.target.checked }),
      value: edit.getValue("isPrebill"),
      padding: "0 24px",
      flexGrow: 0,
    },
    {
      fieldType: INPUT_TYPES.CUSTOM,
      groupable: false,
      name: "sectionHeaderInvoicing",
      width: "100%",
      customComponent: (
        <MiSectionHeader
          title="Invoicing"
          leftIcon={mdiCircleMedium}
          variant={SECTION_HEADER_VARIANTS.CONTAINED}
        />
      ),
    },
    {
      label: "Requires Fulfillment Prior to Invoicing",
      name: "fulfillmentRequiredForInvoicing",
      width: "50%",
      readOnly: !isPaymentMethodSelected,
      fieldType: INPUT_TYPES.CHECKBOX,
      validationRule: z.boolean().optional(),
      padding: "0px 24px",
    },
  ];

  edit.setValidation([...detailFields, ...accountingFields]);
  const onCancel = () => {
    edit.reset();
  };

  return (
    <>
      <MiBox>
        <MiDetailFields
          headerProps={{
            icon: mdiFormatListBulleted,
            title: "Detail Fields",
          }}
          detailFields={detailFields}
          customWrapperStyle={{ gap: 8 }}
          edit={edit}
        />
        <MiDetailFields
          headerProps={{
            icon: mdiAbacus,
            title: "Accounting",
          }}
          customWrapperStyle={classes.customWrapper}
          detailFields={accountingFields}
          edit={edit}
        />
      </MiBox>
      <Footer
        show={edit.isDirty()}
        handleCancelClick={onCancel}
        handleSaveClick={() => {
          const { autoComplete, ...data } = edit.edits;
          onUpdate(data);
          edit.reset();
        }}
        disableSave={!edit.isValid()}
      />
    </>
  );
}

const useStyles = makeStyles()((theme) => ({
  customWrapper: {
    paddingLeft: theme.spacing(3),
  },
}));
